<section class="payment-plans-page relative min-h-screen overflow-hidden bg-[#fffcf9] px-4 pb-20 sm:px-6 lg:px-8">

  <div class="relative mx-auto max-w-7xl">
    <!-- Heading -->
    <header class="mx-auto max-w-2xl text-center">
      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#8c52ff]">Membership plans</p>
      <h1 class="mt-4 font-sans text-3xl font-semibold tracking-tight text-[#111111] sm:text-4xl lg:text-5xl">
        Find your home. <span class="plan-heading-accent">Choose your advantage.</span>
      </h1>
      <p class="mt-4 text-base leading-relaxed text-[#595959] sm:text-lg">
        From discovering exceptional homes to making your next move, choose the intelligence and support you need.
      </p>
    </header>

    <!-- Plans -->
    <div role="radiogroup" aria-label="Payment plans"
         class="mt-12 grid grid-cols-1 gap-6 sm:mx-auto sm:max-w-xl md:max-w-none md:grid-cols-2 lg:mt-16 lg:grid-cols-4 lg:gap-5 xl:gap-6">
      <!-- Free / Basic plan (informational, not selectable) -->
      <div [attr.aria-label]="freePlan.name + ' plan, free'"
           class="plan-shell basic-plan relative flex h-full flex-col overflow-hidden rounded-[28px] border-2 border-dashed border-black/10 bg-white/70 text-left">
        <div class="plan-card-header relative bg-gradient-to-br from-white to-[#f3f0f5] px-6 pb-7 pt-6">
          <div class="flex min-h-[1.5rem] items-center">
            <span class="rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#595959] ring-1 ring-stone-200">
              {{ currentPlanId() === 'basic' ? 'Current plan' : 'Free' }}
            </span>
          </div>
          <span class="mobile-plan-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m3 10 9-7 9 7v11h-6v-7H9v7H3Z"/></svg></span>
          <h2 class="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#595959]">{{ freePlan.name }}</h2>
          <p class="mt-3 flex flex-wrap items-baseline gap-x-1.5">
            <span class="text-4xl font-semibold tracking-tight text-[#111111] lg:text-3xl xl:text-4xl">{{ formatPrice(freePlan.price) }}</span>
          </p>
          <p class="mt-2 text-sm text-black/50">{{ freePlan.tagline }}</p>
        </div>

        <button type="button" class="details-toggle" (click)="toggleDetails('basic')"
                [attr.aria-expanded]="expandedPlanId() === 'basic'" aria-controls="details-basic">
          <span>{{ expandedPlanId() === 'basic' ? 'Hide details' : 'View plan details' }}</span>
          <svg [class.is-open]="expandedPlanId() === 'basic'" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m5 7.5 5 5 5-5" /></svg>
        </button>
        <div id="details-basic" class="plan-details" [class.is-expanded]="expandedPlanId() === 'basic'">
          <div class="details-inner"><ng-container *ngTemplateOutlet="featureList; context: { $implicit: freePlan.features }" /></div>
        </div>

        <div class="px-6 pb-6">
          <div class="flex items-center justify-center rounded-full bg-stone-100 py-3 text-sm font-semibold text-black/50">
            {{ currentPlanId() === 'basic' ? 'Current plan' : 'Included for free users' }}
          </div>
        </div>
      </div>

      @for (plan of plans; track plan.id; let i = $index) {
        <div class="plan-shell group relative flex h-full flex-col overflow-hidden rounded-[28px] border-2 bg-white text-left transition-all duration-300 motion-reduce:transition-none"
             [attr.data-plan]="plan.id"
             [class.is-available]="currentPlanId() !== plan.id && !isDowngrade(plan.id)"
             [class.is-unavailable]="isDowngrade(plan.id)"
             [class.is-selected]="isSelected(plan.id)"
             [class]="isSelected(plan.id) ? plan.theme.selected : 'border-black/[0.08] shadow-sm hover:border-stone-300'">
          <button #planCard type="button" role="radio" class="plan-summary text-left focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-[#8c52ff]"
                  [attr.aria-checked]="isSelected(plan.id)"
                  [disabled]="currentPlanId() === plan.id || isDowngrade(plan.id)"
                  [attr.aria-disabled]="currentPlanId() === plan.id || isDowngrade(plan.id)"
                  [attr.aria-label]="plan.name + ' plan, ' + formatPrice(plan.price) + ' ' + plan.billingPeriod"
                  [tabIndex]="tabIndexFor(plan.id, i)" (click)="selectPlan(plan.id)" (keydown)="onCardKeydown($event, i)">
          <!-- Header band -->
          <div class="plan-card-header relative px-6 pb-7 pt-6 lg:px-7" [class]="plan.theme.header">
            <!-- Radio indicator -->
            <span aria-hidden="true"
                  class="plan-selection-indicator absolute right-5 top-5 flex h-7 w-7 items-center justify-center rounded-full border-2 transition-all duration-300"
                  [class]="isSelected(plan.id) ? plan.theme.indicatorOn : plan.theme.indicatorOff">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"
                   class="h-3.5 w-3.5 transition-transform duration-300"
                   [class]="isSelected(plan.id) ? 'scale-100' : 'scale-50'">
                <path d="M5 10.5l3.2 3.2L15 7" />
              </svg>
            </span>

            <div class="flex min-h-[1.5rem] items-center gap-2 pr-7">
              @if (currentPlanId() === plan.id) {
                <span class="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-[#7439e6] ring-1 ring-[#8c52ff]/20">Current plan</span>
              }
              @if (plan.badge && currentPlanId() !== plan.id) {
                <span class="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider" [class]="plan.theme.badge">
                  {{ plan.badge }}
                </span>
              }
            </div>

            <span class="mobile-plan-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                @switch (plan.id) {
                  @case ('premium') { <path d="M5 20v-6m7 6V9m7 11V4"/> }
                  @case ('intelligence') { <path d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5Z"/> }
                  @case ('advisory') { <circle cx="12" cy="7" r="4"/><path d="M4 21v-2a8 8 0 0 1 16 0v2"/> }
                }
              </svg>
            </span>
            <h2 class="mt-3 text-sm font-semibold uppercase tracking-[0.18em]" [class]="plan.theme.title">
              {{ plan.name }}
            </h2>
            <p class="mt-3 flex flex-wrap items-baseline gap-x-1.5">
              <span class="text-4xl font-semibold tracking-tight lg:text-3xl xl:text-4xl" [class]="plan.theme.price">
                {{ formatPrice(plan.price) }}
              </span>
              <span class="text-sm font-medium" [class]="plan.theme.muted">{{ plan.billingPeriod }}</span>
            </p>
            <p class="mt-2 text-sm" [class]="plan.theme.muted">{{ plan.tagline }}</p>
            @if (plan.id === 'intelligence' && currentPlanId() !== 'intelligence') {
              <div class="intelligence-popularity" style="display: flex; align-items: center; gap: 8px; margin-top: 12px; padding: 8px 10px; border: 1px solid #e2d4f5; border-radius: 8px; background: #ede3fc; color: #7042a7; font-size: 11px; line-height: 1.5; font-weight: 500;">
                <svg width="18" height="18" style="display: block; width: 18px; height: 18px; flex: 0 0 18px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="7" r="3"/><path d="M3 21v-3a6 6 0 0 1 12 0v3H3Zm14-17a3 3 0 0 1 0 6m2 11h3v-3a6 6 0 0 0-4-5"/></svg>
                <span>8 in 10 homebuyers choose this</span>
              </div>
            }
          </div>

          </button>
          <button type="button" class="details-toggle" (click)="toggleDetails(plan.id)"
                  [attr.aria-expanded]="expandedPlanId() === plan.id" [attr.aria-controls]="'details-' + plan.id">
            <span>{{ expandedPlanId() === plan.id ? 'Hide details' : 'View plan details' }}</span>
            <svg [class.is-open]="expandedPlanId() === plan.id" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m5 7.5 5 5 5-5" /></svg>
          </button>
          <div [id]="'details-' + plan.id" class="plan-details" [class.is-expanded]="expandedPlanId() === plan.id">
            <div class="details-inner"><ng-container *ngTemplateOutlet="featureList; context: { $implicit: plan.features }" /></div>
          </div>

          <!-- Card footer state -->
          <div class="px-6 pb-6 lg:px-7">
            <button type="button" [disabled]="currentPlanId() === plan.id || isDowngrade(plan.id)" (click)="selectPlan(plan.id)" class="w-full flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold transition-colors duration-300"
                 [class]="isSelected(plan.id)
                   ? plan.theme.footerOn
                   : 'bg-stone-100 text-[#595959] group-hover:bg-stone-200'">
              {{ currentPlanId() === plan.id ? 'Selected Plan' : isDowngrade(plan.id) ? 'Unavailable' : isSelected(plan.id) ? 'Selected' : 'Select plan' }}
            </button>
          </div>
        </div>
      }
    </div>

    <section class="mobile-plan-comparison" aria-labelledby="mobile-plan-details-title">
      <div class="mobile-details-heading">
        <span>THE DETAILS</span>
        <h2 id="mobile-plan-details-title">What’s included</h2>
        <p>Explore the benefits of each plan.</p>
      </div>
      <details class="mobile-plan-benefits" [open]="expandedPlanId() === 'basic' || (!selectedPlanId() && currentPlanId() === 'basic')">
        <summary><span>{{ freePlan.name }} <small>Free access</small></span><svg class="benefits-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg></summary>
        <ng-container *ngTemplateOutlet="featureList; context: { $implicit: freePlan.features }" />
      </details>
      @for (plan of plans; track plan.id) {
        <details class="mobile-plan-benefits" [class.is-highlighted]="isSelected(plan.id)" [open]="expandedPlanId() === plan.id || isSelected(plan.id)">
          <summary><span>{{ plan.name }} <small>{{ formatPrice(plan.price) }} {{ plan.billingPeriod }}</small></span><svg class="benefits-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg></summary>
          <ng-container *ngTemplateOutlet="featureList; context: { $implicit: plan.features }" />
          @if (currentPlanId() !== plan.id && !isDowngrade(plan.id)) {
            <button class="mobile-benefits-select" type="button" (click)="selectPlan(plan.id)">{{ isSelected(plan.id) ? 'Selected' : 'Select ' + plan.name }}</button>
          }
        </details>
      }
    </section>

    <!-- Continue bar: pinned on mobile, inline on desktop -->
    <div class="payment-continue-bar sticky bottom-0 z-10 -mx-4 mt-10 border-t border-black/[0.07] bg-[#fffcf9]/85 px-4 py-4 backdrop-blur-md sm:-mx-6 sm:px-6 lg:static lg:mx-0 lg:mt-14 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
      <div class="mx-auto flex max-w-3xl flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between lg:rounded-2xl lg:border lg:border-black/10 lg:bg-white lg:px-6 lg:py-4 lg:shadow-sm">
        <p class="text-center text-sm text-[#595959] sm:text-left" aria-live="polite">
          @if (selectionMessage()) {
            <span class="plan-selection-error" role="alert">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v6m0 3v1"/></svg>
              {{ selectionMessage() }}
            </span>
          } @else if (selectedPlan()) {
            Selected: <span class="font-semibold text-[#111111]">{{ selectedPlan()!.name }}</span>
            <span class="text-black/40"> · </span>
            <span class="font-semibold text-[#111111]">{{ formatPrice(selectedPlan()!.price) }}</span>
            <span class="text-black/50"> {{ selectedPlan()!.billingPeriod }}</span>
          } @else {
            Select a plan to continue
          }
        </p>

        <button type="button"
                (click)="continue()"
                [disabled]="isNavigating()"
                class="group inline-flex items-center justify-center gap-2 rounded-full theme-btn1 bg-[#8c52ff] px-8 py-3.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#7439e6] hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8c52ff]/30 active:translate-y-0 disabled:cursor-not-allowed disabled:bg-stone-300 disabled:text-black/50 disabled:shadow-none disabled:hover:translate-y-0">
          @if (isNavigating()) {
            <span class="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true"></span>
            Processing…
          } @else {
            Continue
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                 class="h-4 w-4 transition-transform duration-200 group-enabled:group-hover:translate-x-1" aria-hidden="true">
              <path d="M4 10h12M11 5l5 5-5 5" />
            </svg>
          }
        </button>
      </div>
    </div>
  </div>

  <!-- Shared feature list used by every card -->
  <ng-template #featureList let-features>
    <ul class="plan-feature-list flex-1 divide-y divide-black/[0.07] px-6 py-3 lg:px-7">
      @for (feature of features; track feature.label) {
      <li class="flex items-start gap-3 py-3">
        @if (feature.included) {
          <span class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#8c52ff] text-white">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3" aria-hidden="true">
              <path d="M5 10.5l3.2 3.2L15 7" />
            </svg>
          </span>
        } @else {
          <span class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
            <span class="h-px w-3 bg-stone-300"></span>
          </span>
        }
        <span class="min-w-0">
          <span class="block text-sm font-medium" [class]="feature.included ? 'text-[#111111]' : 'text-black/40'">
            {{ feature.label }}
            @if (feature.value && feature.included) {
              <span class="ml-1 font-semibold text-[#8c52ff]">· {{ feature.value }}</span>
            }
            <span class="sr-only">{{ feature.included ? '(included)' : '(not included)' }}</span>
          </span>
          <span class="mt-0.5 block text-xs" [class]="feature.included ? 'text-black/50' : 'text-black/35'">
            {{ feature.description }}
          </span>
        </span>
      </li>
      }
    </ul>
  </ng-template>
</section>
